import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { JawType } from '../../../../types/admin';
import styles from '../StepPanel.module.scss';
import { AlignerProgressRow } from './AlignerProgressRow';
import { type AlignerSet, resolveDisplayStatus } from './buildPatch';

type JawProgressSectionProps = {
  readonly jaw: JawType;
  readonly patientId: string;
  readonly treatmentId: string;
  readonly cycleId: string;
  readonly aligners: readonly AlignerSet[];
};

export function JawProgressSection({
  jaw,
  patientId,
  treatmentId,
  cycleId,
  aligners,
}: JawProgressSectionProps) {
  const { t } = useTranslation();
  const wearingAligner = aligners.find((a) => resolveDisplayStatus(a) === 'wearing');
  const pausedAligner = aligners.find((a) => resolveDisplayStatus(a) === 'paused');
  const activeAligner = wearingAligner ?? pausedAligner;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (aligners.length === 0) return null;

  const headerKey =
    jaw === 'upper' ? 'wearing.progress.upperHeader' : 'wearing.progress.lowerHeader';

  const summaryText = activeAligner
    ? t('wearing.progress.summaryActive', { number: activeAligner.aligner_number })
    : t('wearing.progress.summaryIdle', { total: aligners.length });

  return (
    <div className={styles.progressGroup}>
      <button
        type="button"
        className={styles.jawSectionToggle}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span
          className={`${styles.jawSectionToggleChevron} ${
            isExpanded ? styles.jawSectionToggleChevronOpen : ''
          }`}
          aria-hidden="true"
        />
        <span className={styles.jawSectionToggleTitle}>{t(headerKey)}</span>
        <span className={styles.jawSectionToggleSummary}>{summaryText}</span>
      </button>
      {isExpanded && (
        <div className={styles.jawSectionBody}>
          {aligners.map((aligner) => (
            <AlignerProgressRow
              key={aligner.id}
              aligner={aligner}
              patientId={patientId}
              treatmentId={treatmentId}
              cycleId={cycleId}
              isEditing={editingId === aligner.id}
              onToggleEdit={() =>
                setEditingId((prev) => (prev === aligner.id ? null : aligner.id))
              }
              onCloseEdit={() => setEditingId(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
